import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, input, signal, type WritableSignal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { DiagnosticRating, PlatformKey } from '@malou-io/package-utils';

import { RatingBlocComponent } from ':modules/rating-bloc/rating-bloc.component';
import { PHONE_SCREEN_BREAKPOINT } from ':shared/constants';
import { ScoreCriteria } from ':shared/enums';
import { SvgIcon } from ':shared/enums/svg-icon.enum';
import { AggregatedDiagnostics, type Diagnostic } from ':shared/models';
import { ApplyPurePipe, Illustration, IllustrationPathResolverPipe, ImagePathResolverPipe, PluralTranslatePipe } from ':shared/pipes';
import { LogoPathResolverPipe } from ':shared/pipes/logo-path-resolver.pipe';

@Component({
    selector: 'app-aggregated-diagnostics-score',
    imports: [
        NgClass,
        NgTemplateOutlet,
        RatingBlocComponent,
        MatIconModule,
        TranslateModule,
        IllustrationPathResolverPipe,
        ImagePathResolverPipe,
        PluralTranslatePipe,
        LogoPathResolverPipe,
        ApplyPurePipe,
    ],
    templateUrl: './aggregated-diagnostics-score.component.html',
    styleUrl: './aggregated-diagnostics-score.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AggregatedDiagnosticsScoreComponent {
    readonly DiagnosticRating = DiagnosticRating;
    readonly Illustration = Illustration;
    readonly SvgIcon = SvgIcon;
    readonly PlatformKey = PlatformKey;
    readonly ScoreCriteria = ScoreCriteria;
    readonly MAXIMUM_NUMBER_OF_RESTAURANTS_TO_SHOW_DETAILS = 3;

    readonly isPreview = input<boolean>(true);
    readonly diagnostics = input.required<Diagnostic[]>();

    readonly isPhoneScreen: WritableSignal<boolean> = signal(false);
    readonly instagramImageLoadError: WritableSignal<boolean> = signal(false);

    readonly aggregatedDiagnostics = computed(() => new AggregatedDiagnostics(this.diagnostics()));

    readonly restaurantsDiagnosticsData = computed(() => this.aggregatedDiagnostics().getDiagnosticsData());

    readonly aggregatedScore = computed(() => this.aggregatedDiagnostics().getTotalScore());
    readonly aggregatedScoreGaugeImage = computed(() => this.aggregatedDiagnostics().getTotalScoreGaugeImage());
    readonly aggregatedScoreRating = computed(() => this.aggregatedDiagnostics().getTotalScoreRating());

    readonly isExactlyXRestaurants = (x: number) => this.restaurantsDiagnosticsData().length === x;

    getRestaurantSortedScoreCriterions = (diagnosticId: string): ScoreCriteria[] => {
        const restaurantScoreCriterions = this.aggregatedDiagnostics().getRestaurantScoreCriterions(diagnosticId);
        if (this.isExactlyXRestaurants(3)) {
            return restaurantScoreCriterions.getSortedScoreCriterions().slice(0, 1);
        }
        return restaurantScoreCriterions.getSortedScoreCriterions().slice(0, 2);
    };

    getRestaurantScoreCriterionDetails = (diagnosticId: string, scoreCriterion: ScoreCriteria) => {
        const restaurantScoreCriterions = this.aggregatedDiagnostics().getRestaurantScoreCriterions(diagnosticId);
        return restaurantScoreCriterions.getScoreCriteriaDetails(scoreCriterion);
    };

    onInstagramImageLoadError() {
        this.instagramImageLoadError.set(true);
    }

    @HostListener('window:resize')
    onResize() {
        this.isPhoneScreen.set(window.innerWidth < PHONE_SCREEN_BREAKPOINT);
    }
}
